import { computed } from '@vue/composition-api';
import orderBy from 'lodash/orderBy';
import { useStorymapStore } from '../useModule';
import { StorymapState, Subtask, SubtaskStatus } from '../types';
import { filteredSubtasks } from './useFilterCommon';

export interface TrackingVisibleStatusColumn {
    status: SubtaskStatus;
    cards: Subtask[];
}

export const sortTrackingCardsInVisibleOrder = (cards: Subtask[]): Subtask[] => {
    return [...cards].sort((left, right) => left.releaseViewOrder - right.releaseViewOrder);
};

export const getTrackingVisibleStatuses = (state: StorymapState): SubtaskStatus[] => {
    return orderBy(state.subtaskStatuses, [(status) => status.category, (status) => status.order]);
};

export const getTrackingVisibleCards = (state: StorymapState, releaseId: string | null = state.trackingViewState.releaseId): Subtask[] => {
    if (!releaseId) {
        return [];
    }

    return filteredSubtasks.value
        .filter((card) => card.releaseId === releaseId)
        .filter(
            (card) =>
                state.trackingViewState.filterTaskIds.length === 0 ||
                state.trackingViewState.filterTaskIds.includes(card.taskId)
        );
};

export const getTrackingVisibleStatusColumns = (
    state: StorymapState,
    releaseId: string | null = state.trackingViewState.releaseId
): TrackingVisibleStatusColumn[] => {
    const visibleCards = getTrackingVisibleCards(state, releaseId);

    return getTrackingVisibleStatuses(state).map((status) => ({
        status,
        cards: sortTrackingCardsInVisibleOrder(visibleCards.filter((card) => card.status === status.key)),
    }));
};

export const getTrackingVisibleCardIds = (
    state: StorymapState,
    releaseId: string | null = state.trackingViewState.releaseId
): string[] => {
    return getTrackingVisibleStatusColumns(state, releaseId).flatMap((column) => column.cards.map((card) => card.id));
};

export const getFirstTrackingVisibleCard = (
    state: StorymapState,
    releaseId: string | null = state.trackingViewState.releaseId
): Subtask | null => {
    return getTrackingVisibleStatusColumns(state, releaseId).find((column) => column.cards.length > 0)?.cards[0] ?? null;
};

export const getTrackingVisibleCardPosition = (
    state: StorymapState,
    cardId: string,
    releaseId: string | null = state.trackingViewState.releaseId
): { columnIndex: number; cardIndex: number } | null => {
    const columns = getTrackingVisibleStatusColumns(state, releaseId);

    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        const cardIndex = columns[columnIndex].cards.findIndex((card) => card.id === cardId);

        if (cardIndex >= 0) {
            return { columnIndex, cardIndex };
        }
    }

    return null;
};

export const trackingVisibleStatusColumns = computed(() => getTrackingVisibleStatusColumns(useStorymapStore().state));
export const trackingVisibleCardIds = computed(() => getTrackingVisibleCardIds(useStorymapStore().state));
